import { Sensor, useHVACData } from 'common';
import { useTranslation } from 'react-i18next';
import { YStack, Text, XStack } from 'tamagui';
import { tokens } from 'tamagui-config';

import { HubLineChart } from '../../../components/common/HubLineChart';
import { LoadingOverlay } from '../../../components/common/LoadingOverlay';
import { isDateStale } from '../../../utils/dateUtils';

interface HumidityStatus {
  value: number;
  status: 'dry' | 'normal' | 'slightly_humid' | 'humid' | 'very_humid';
  color: string;
}

interface HVACHumidityContentProps {
  sensor?: Sensor;
}

const getHumidityStatus = (humidity: number): HumidityStatus => {
  if (humidity <= 29) return { value: humidity, status: 'dry', color: tokens.color.warning };
  if (humidity <= 50) return { value: humidity, status: 'normal', color: tokens.color.textLight };
  if (humidity <= 60)
    return { value: humidity, status: 'slightly_humid', color: tokens.color.warning };
  if (humidity <= 70) return { value: humidity, status: 'humid', color: tokens.color.energyRed };
  return { value: humidity, status: 'very_humid', color: tokens.color.energyRed };
};

export const HVACHumidityContent: React.FC<HVACHumidityContentProps> = ({ sensor }) => {
  const { t } = useTranslation();
  const { humidityData, currentHumidity, lastReported, loading } = useHVACData(sensor);
  
  const humidityUnit = '%';
  const humidityStatus =
    currentHumidity !== undefined ? getHumidityStatus(currentHumidity) : undefined;

  // Check if data is stale (more than 24 hours old)
  const isHumidityDataStale = lastReported ? isDateStale(lastReported) : false;

  return (
    <YStack position="relative" flex={1}>
      <LoadingOverlay isVisible={loading} />

      {!loading && currentHumidity === undefined && humidityData.length === 0 && (
        <XStack flex={1} justifyContent="center" alignItems="center">
          <Text fontSize="$6" color={tokens.color.textLight} textAlign="center">
            {t('hvac.noHumidityData')}
          </Text>
        </XStack>
      )}

      {humidityStatus && (
        <YStack>
          <Text
            fontFamily="$heading"
            fontWeight="$9.5"
            fontSize="$9.5"
            color={tokens.color.white12}>
            {isHumidityDataStale 
              ? t('hvac.waitingForNewData', 'Waiting for new data')
              : `${Number(currentHumidity).toFixed(0)}${humidityUnit}`
            }
          </Text>
          <Text fontSize="$6" fontWeight="$5" color={humidityStatus.color}>
            {isHumidityDataStale 
              ? t('hvac.leakDetectorInactive', 'Inactive') 
              : t(`hvac.humidityLevel.${humidityStatus.status}`)
            }
          </Text>
        </YStack>
      )}

      {humidityData.length > 0 && (
        <HubLineChart data={humidityData as any} minValue={0} maxValue={100} unit={humidityUnit} />
      )}
    </YStack>
  );
};
